import { useState, useEffect, useRef } from "react";
import { getUsers } from "../services/userService";
import useDebounce from "./useDebounce";
import { UserOption } from "./useUsersByRole";

interface RawUser {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: string;
  [key: string]: unknown;
}

interface LoadParams {
  branchId: string;
  search: string;
  page: number;
}

function normalizeUser(u: RawUser): UserOption {
  return {
    id: String(u.id),
    firstName: String(u.firstName ?? ""),
    lastName: String(u.lastName ?? ""),
    email: String(u.email ?? ""),
    phone: String(u.phone ?? ""),
    address: String(u.address ?? ""),
    primaryContact: String((u as Record<string, unknown>).primaryContact ?? ""),
  };
}

export function useLenderOfficers(branchId?: string) {
  const [options, setOptions] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState("");
  // branchId is stored inside LoadParams so the fetch effect does NOT depend on the branchId prop
  const [loadParams, setLoadParams] = useState<LoadParams | null>(null);
  const fetchedBranchRef = useRef<string | null>(null);

  const debouncedSearch = useDebounce(search, 400);

  // Reset when branch changes — explicitly clear loading so a stale true never leaks through
  useEffect(() => {
    setOptions([]);
    setHasMore(false);
    setLoading(false);
    setSearch("");
    setLoadParams(null);
    fetchedBranchRef.current = null;
  }, [branchId]);

  // Re-search when the user types, but only after the initial fetch has been triggered
  useEffect(() => {
    if (fetchedBranchRef.current && fetchedBranchRef.current === branchId) {
      setLoadParams({ branchId: fetchedBranchRef.current, search: debouncedSearch, page: 1 });
    }
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch only when loadParams change — branchId is NOT a dependency here
  useEffect(() => {
    if (!loadParams) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      const params: Record<string, unknown> = {
        status: "active",
        limit: 50,
        lenderId: loadParams.branchId,
        page: loadParams.page,
      };
      if (loadParams.search.trim()) params.q = loadParams.search.trim();

      const { data, error } = (await getUsers(params)) as {
        data: { data?: RawUser[]; pagination?: { total: number } } | null;
        error: unknown;
      };

      if (cancelled) return;

      if (!error) {
        const list: RawUser[] = Array.isArray(data?.data) ? data!.data! : [];
        const normalized = list.map(normalizeUser);
        const total = data?.pagination?.total ?? 0;
        setOptions((prev) => {
          const updated = loadParams.page === 1 ? normalized : [...prev, ...normalized];
          setHasMore(updated.length < total);
          return updated;
        });
      }
      setLoading(false);
    };

    fetchData();
    return () => { cancelled = true; };
  }, [loadParams]); // branchId intentionally omitted — it lives inside loadParams

  const triggerFetch = () => {
    if (!branchId || fetchedBranchRef.current === branchId) return;
    fetchedBranchRef.current = branchId;
    setLoadParams({ branchId, search: "", page: 1 });
  };

  return {
    options,
    loading,
    hasMore,
    triggerFetch,
    onSearch: (value: string) => setSearch(value),
    onLoadMore: () =>
      setLoadParams((prev) => (prev ? { ...prev, page: prev.page + 1 } : prev)),
  };
}
